import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppSelect from '@nxsys/components/AppSelect';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Option } from '@nxsys/types/models/Common';
import { irelandCountyOptions } from '@nxsys/constants/AppConstants';
import { countryList } from '@nxsys/utils';
import { Col, Form, Row } from 'antd';
import { FormProps } from 'antd/lib';
import { Ref, useImperativeHandle } from 'react';
import IconWrapper from './IconWrapper';
import StepTitle from './StepTitle';
import {
  formatPostCode,
  postCodeValidationRule,
} from '@nxsys/utils/formFieldValidation';
import { useSelector } from 'react-redux';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';

export type CompanyDetailValues = {
  companyName: string;
  tradingName: string;
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  city: string;
  county: string;
  postCode: string;
  country: Option<string> | null;
};

export type CompanyDetailFormRef = {
  isValidForm: () => Promise<boolean>;
  getValues: () => CompanyDetailValues;
  resetFields: () => void;
};

type Props = {
  formRef: Ref<CompanyDetailFormRef>;
  initialValues: Partial<CompanyDetailValues>;
  stepNumber: number;
};

const CompanyDetail: React.FC<Props> = ({
  formRef,
  initialValues,
  stepNumber,
}) => {
  const { messages } = useTypedIntl();
  const [form] = Form.useForm<CompanyDetailValues>();
  const { payrollCountry, isBureau } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;

  useImperativeHandle(formRef, () => ({
    isValidForm: async () => {
      try {
        await form.validateFields();
        return true;
      } catch (error) {
        return false;
      }
    },
    getValues() {
      return form.getFieldsValue(true);
    },
    resetFields() {
      form.resetFields();
    },
  }));

  const onFinish: FormProps['onFinish'] = async (values) => {
    console.info('values', values);
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <div className="CompanyDetail">
      <StepTitle
        step={stepNumber}
        title={isBureau ? 'Employer Detail' : 'Company Detail'}
        description={isBureau ? 'Fill in your employer information!' : 'Fill in your company information!'}
      />
      <div className="mt-[24px]">
        <AppFormContainer
          form={form}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          initialValues={initialValues}
          layout="vertical"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
        >
          {/* Company Name Section */}
          <div className="mb-[24px]">
            <div className="flex items-center gap-x-[8px] mb-[16px]">
              <IconWrapper icon="IconBuilding" />
              <div className="text-[16px] font-semibold">{isBureau ? 'Employer Name' : 'Company Name'}</div>
            </div>
            <div className="space-y-[16px]">
              <Form.Item
                label={isBureau ? 'Employer Name' : 'Company Name'}
                name="companyName"
                rules={[
                  {
                    required: true,
                    message:
                      messages['form.pleaseEnter'] ||
                      isBureau ? 'Please enter employer name' : 'Please enter company name',
                  },
                ]}
              >
                <AppInput placeholder={isBureau ? 'Enter employer name' : 'Enter company name'} />
              </Form.Item>
              <Form.Item
                label="Trading Name"
                name="tradingName"
                rules={[
                  {
                    required: true,
                    message:
                      messages['form.pleaseEnter'] ||
                      'Please enter trading name',
                  },
                ]}
              >
                <AppInput placeholder="Enter trading name" />
              </Form.Item>
            </div>
          </div>

          {/* Address Section */}
          <div>
            <div className="flex items-center gap-x-[8px] mb-[16px]">
              <IconWrapper icon="IconLocationPin" />
              <div className="text-[16px] font-semibold">Address</div>
            </div>
            <div className="space-y-[16px]">
              <Form.Item
                label="Address Line 1"
                name="addressLine1"
                rules={[
                  {
                    required: true,
                    message:
                      messages['form.pleaseEnter'] || 'Please enter address',
                  },
                ]}
              >
                <AppInput placeholder="Enter address" />
              </Form.Item>
              <Form.Item
                label="Address Line 2"
                name="addressLine2"
                rules={[
                  {
                    required: true,
                    message:
                      messages['form.pleaseEnter'] || 'Please enter address',
                  },
                ]}
              >
                <AppInput placeholder="Enter address" />
              </Form.Item>
              <Form.Item
                label="Address Line 3"
                name="addressLine3"
                // rules={[
                //   {
                //     required: true,
                //     message:
                //       messages['form.pleaseEnter'] || 'Please enter address',
                //   },
                // ]}
              >
                <AppInput placeholder="Enter address" />
              </Form.Item>
              <Row gutter={16}>
                <Col sm={24} md={12}>
                  <Form.Item
                    label={messages['common.cityTown'] || 'City/Town'}
                    name="city"
                  >
                    <AppInput placeholder="Enter city" maxLength={50} />
                  </Form.Item>
                </Col>
                <Col sm={24} md={12}>
                  <Form.Item
                    label={messages['common.county'] || 'County'}
                    name="county"
                  >
                    {isIreland ? (
                      <AppSelect
                        showSearch
                        placeholder="Select county"
                        options={irelandCountyOptions}
                      />
                    ) : (
                      // Both columns are varchar(50); the DTO rejects longer at
                      // submit, by which point the whole wizard is filled in.
                      <AppInput placeholder="Enter county" maxLength={50} />
                    )}
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col sm={24} md={12}>
                  <Form.Item
                    label={isIreland ? 'EIR Code' : 'Post Code'}
                    name="postCode"
                    rules={[
                      {
                        required: true,
                        message:
                          messages['form.pleaseEnter'] ||
                          'Please enter postcode',
                      },
                      postCodeValidationRule(messages, payrollCountry),
                    ]}
                  >
                    <AppInput
                      placeholder={
                        isIreland ? 'Enter EIR code' : 'Enter postcode'
                      }
                      upperCaseAll
                      onChange={(e) => {
                        form.setFieldValue(
                          'postCode',
                          formatPostCode(e.target.value, payrollCountry),
                        );
                      }}
                      maxLength={8}
                    />
                  </Form.Item>
                </Col>
                <Col sm={24} md={12}>
                  <Form.Item
                    label="Country"
                    name="country"
                    // rules={[
                    //   {
                    //     required: true,
                    //     message:
                    //       messages['form.pleaseSelect'] ||
                    //       'Please select country',
                    //   },
                    // ]}
                  >
                    <AppSelect
                      placeholder="Select country"
                      options={countryList}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>
          </div>
        </AppFormContainer>
      </div>
    </div>
  );
};

export default CompanyDetail;
